import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { getAppColor } from "@icarius-app/selectors";
import { IconButton, Typography, Paper, Grow, InputAdornment } from "@material-ui/core";
import IcariusIcon from "@icarius-assets/images/icariusIcon.png";
import { CloseIcon, SendIcon } from "@icarius-icons";
import Draggable from "react-draggable";
import { getLocalizedString } from "@icarius-localization/strings";
import useBotDraggable from "@icarius-common/botAssistant/components/useBotDraggable";
import ProfileImg from "@icarius-common/profileImg";
import Message from "@icarius-common/botAssistant/components/message";
import CustomTextField from "@icarius-common/botAssistant/components/customTextField";
import { formatDateHHMM } from "@icarius-utils/date";

const createMessage = (role, content) => ({
    role,
    content,
    time: formatDateHHMM(new Date()),
});

const buildExplainPrompt = (formulaText) => {
    const code = formulaText.replace(/\r\n?/g, "\n").replace(/\t/g, "    ");
    return `La fórmula a analizar es:

\`\`\`
${code}
\`\`\`

¿Puedes explicarme qué hace esta fórmula?`;
};

const FormulaAiDialog = (props) => {
    const { open, isLoading, formulaText, geminiContext, queryPreset, handleSendMessage, handleClose } = props;

    const defaultMessageState = useMemo(() => {
        return [
            createMessage("gemini", { type: "text", format: "plain", text: getLocalizedString("botInitialMessage") }),
        ];
    }, []);

    const [messages, setMessages] = useState(defaultMessageState);
    const [text, setText] = useState("");

    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    const color = useSelector(getAppColor);

    const { dragRef, bounds } = useBotDraggable(open);

    useEffect(() => {
        if (open && scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: "smooth" });
            if (inputRef.current) inputRef.current.focus();
        }
    }, [open, messages]);

    useEffect(() => {
        setMessages(defaultMessageState);
    }, [queryPreset, defaultMessageState]);

    const buildRequestData = useCallback(
        (prompt) => {
            const data = { prompt, origin: "FORMULAS" };
            data.id = queryPreset;

            if (geminiContext) {
                data.conversationId = geminiContext.conversationId;
            } else {
                data.type = "query";
                data.file = new Blob([`Fórmula a analizar:\n${formulaText}`], { type: "text/csv" });
            }

            return data;
        },
        [formulaText, geminiContext, queryPreset]
    );

    const submitPrompt = useCallback(
        async (prompt) => {
            setMessages((prev) => [...prev, createMessage("user", { type: "text", format: "plain", text: prompt })]);

            const requestResponse = await handleSendMessage(buildRequestData(prompt));
            const { status, response } = requestResponse?.data || {};
            if (status !== "OK" || !response) return;

            setMessages((prev) => [...prev, createMessage("gemini", response)]);
        },
        [buildRequestData, handleSendMessage]
    );

    useEffect(() => {
        if (!open || messages.length !== 1 || !formulaText) return;
        submitPrompt(buildExplainPrompt(formulaText));
    }, [open, messages, formulaText, geminiContext, queryPreset, submitPrompt]);

    const sendMessage = async (promptToSend = text) => {
        if (!promptToSend.trim()) return;
        setText("");
        await submitPrompt(promptToSend);
    };

    const submit = (e) => {
        e.preventDefault();
        sendMessage(text);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            submit(e);
        }
    };

    return (
        <>
            {open && (
                <Draggable nodeRef={dragRef} handle=".bot-drag-handle" cancel="button" bounds={bounds}>
                    <div
                        ref={dragRef}
                        style={{
                            position: "fixed",
                            right: 10,
                            bottom: 10,
                            zIndex: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: 10,
                        }}
                    >
                        <Grow in={open} style={{ transformOrigin: "bottom right" }} unmountOnExit>
                            <Paper
                                role="dialog"
                                style={{
                                    borderRadius: 10,
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "calc(100vh - 125px)",
                                    maxHeight: 700,
                                    width: 450,
                                    maxWidth: "calc(100vw - 20px)",
                                    overflow: "hidden",
                                    background: "transparent",
                                }}
                            >
                                <header
                                    className="bot-drag-handle"
                                    style={{
                                        background: color,
                                        padding: 10,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        flexShrink: 0,
                                        cursor: "move",
                                        userSelect: "none",
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <ProfileImg image={IcariusIcon} size={40} external />
                                        <div>
                                            <Typography style={{ fontWeight: 600, color: "white" }}>
                                                {getLocalizedString("icariusAssistant")}
                                            </Typography>
                                            <Typography variant="caption" style={{ color: "white" }}>
                                                {getLocalizedString("howCanIHelp")}
                                            </Typography>
                                        </div>
                                    </div>
                                    <IconButton onClick={handleClose}>
                                        <CloseIcon style={{ fill: "white" }} />
                                    </IconButton>
                                </header>
                                <section
                                    style={{
                                        flex: 1,
                                        minHeight: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        background: "var(--mainBackgroundColor)",
                                        border: "1px solid var(--secondaryBackgroundColor)",
                                        borderRadius: "0px 0px 10px 10px",
                                    }}
                                >
                                    <div
                                        style={{
                                            flex: 1,
                                            minHeight: 0,
                                            padding: 10,
                                            overflowY: "auto",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 10,
                                        }}
                                    >
                                        {messages.map((message, index) => {
                                            return (
                                                <Message
                                                    key={index}
                                                    text={message.content.text}
                                                    status={message.content.type}
                                                    attachments={message.content.attachments}
                                                    time={message.time}
                                                    isUser={message.role === "user"}
                                                />
                                            );
                                        })}
                                        {isLoading && <Message isUser={false} />}
                                        <div ref={scrollRef} />
                                    </div>
                                    <footer
                                        style={{
                                            flexShrink: 0,
                                            padding: 10,
                                            paddingBottom: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: 5,
                                        }}
                                    >
                                        <form onSubmit={submit} style={{ width: "100%" }} noValidate>
                                            <CustomTextField
                                                inputRef={inputRef}
                                                disabled={isLoading}
                                                size={"small"}
                                                placeholder={getLocalizedString("writeYourMessage")}
                                                value={text}
                                                onChange={(e) => setText(e.target.value)}
                                                fullWidth={true}
                                                onKeyDown={handleKeyDown}
                                                multiline={true}
                                                rowsMax={5}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment
                                                            position="end"
                                                            style={{ display: "flex", gap: 5 }}
                                                        >
                                                            <IconButton
                                                                title={getLocalizedString("sendMessage")}
                                                                edge="end"
                                                                type="submit"
                                                                disabled={isLoading}
                                                            >
                                                                <SendIcon style={{ fill: "var(--mainText)" }} />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </form>
                                        <Typography variant="caption" className="whiteText">
                                            {getLocalizedString("doNotShareInformation")}
                                        </Typography>
                                    </footer>
                                </section>
                            </Paper>
                        </Grow>
                    </div>
                </Draggable>
            )}
        </>
    );
};

export default FormulaAiDialog;
